import React, { useEffect, useState } from "react";

import { LOCAL_STORAGE_KEY_USER_THEME } from "@helpers/constants";

import { useAppSelector } from "../../store/hooks";
import { getData as getJobData } from "./api";
import { ErrorView, Header, Loading } from "./components";
import styles from "./Overview.module.css";
import type { ResponseData as JobDataResponse } from "./types";
import OverviewTable from "./views/OverviewTable";
import Theme from "./views/theme";

const setThemePref = (theme: string) => {
  window.localStorage.setItem(LOCAL_STORAGE_KEY_USER_THEME, theme);
};

const getThemePref = () => {
  return window.localStorage.getItem(LOCAL_STORAGE_KEY_USER_THEME);
};

const Overview: React.FC = () => {
  const [hasError, setHasError] = useState<Error | null>(null);
  const [jobData, setJobData] = useState<JobDataResponse | null>(null);
  const [theme, setTheme] = useState<string>(getThemePref() || "Light");
  const auth = useAppSelector((state) => state.auth);
  const orgId = auth.user?.organization;

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        const data = await getJobData();
        setJobData(data);
      } catch (error) {
        console.error(error);
        setHasError(error as Error);
      }
    };

    fetchJobData();
  }, []); // Empty dependency array means this runs once on mount

  const toggleTheme = () => {
    setTheme((state) => {
      const theme = state === "Light" ? "Dark" : "Light";
      setThemePref(theme);
      return theme;
    });
  };

  if (hasError) {
    return <ErrorView error={hasError} />;
  }

  const themeObj = Theme[theme as keyof typeof Theme] || {};

  return (
    <Theme variables={themeObj as { [key: string]: string }}>
      <div className={styles.appBody}>
        <Header orgId={orgId} curTheme={theme} toggleTheme={toggleTheme} />

        <div className={styles.content}>
          {jobData ? <OverviewTable responseData={jobData} /> : <Loading />}
        </div>
      </div>
    </Theme>
  );
};

export default Overview;
